import React from 'react';
import { Button, Form, Input, Modal, Result } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';

import Particles from "react-tsparticles";
import type { Engine, RecursivePartial, IOptions } from "tsparticles-engine";
import { loadFull } from "tsparticles";

import { motion, useIsPresent } from 'framer-motion';

import useAuth from '../../hooks/useAuth';
import useNoti from '../../hooks/useNoti';


// const { Title, Text } = Typography;

const particleOptions: RecursivePartial<IOptions> = {
    background: {
        color: {
            value: "rgb(26, 37, 47)",
        },
    },
    fpsLimit: 60,
    interactivity: {
        events: {
            onClick: {
                enable: false,
                mode: "push",
            },
            onHover: {
                enable: false,
                mode: "repulse",
            },
            resize: true,
        },
        modes: {
            push: {
                quantity: 4,
            },
            repulse: {
                distance: 200,
                duration: 0.4,
            },
        },
    },
    particles: {
        color: { value: '#FF8300' },
        links: {
            color: "#FF8300",
            distance: 150,
            enable: true,
            opacity: 0.5,
            width: 1,
        },
        collisions: {
            enable: false,
        },
        move: {
            direction: "none",
            enable: true,
            outModes: {
                default: "out",
            },
            random: true,
            speed: 3,
            straight: false,
        },
        number: {
            density: {
                enable: true,
                area: 800,
            },
            value: 80,
        },
        opacity: {
            value: 0.5,
        },
        shape: {
            type: "circle",
        },
        size: {
            value: { min: 1, max: 5 },
        },
    }
}

interface SignupPayload {
    email: string;
}

const SignupPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAuth();
    const isPresent = useIsPresent();
    const noti = useNoti();
    const [form] = Form.useForm();

    const [isLoading, setIsLoading] = React.useState<boolean | undefined>(false);
    const [openAgreementModal, setOpenAgreementModal] = React.useState(false);
    const [openSuccessSignupModal, setOpenSuccessSignupModal] = React.useState(false);    
    
    const particlesInit = React.useCallback(async (engine: Engine) => {
        //console.log(engine);

        // you can initialize the tsParticles instance (engine) here, adding custom shapes or presets
        // this loads the tsparticles package bundle, it's the easiest method for getting everything ready
        // starting from v2 you can add only the features you need reducing the bundle size
        await loadFull(engine);
    }, []);

    if (auth && auth?.hasToken) {
        return <Navigate to="/dashboard" state={{ from: location }} />
        //navigate('/dashboard', { replace: true })
    }

    const onFinish = async (values: SignupPayload) => {
        setOpenAgreementModal(true);
    }

    const handleAcceptAgreementClick = async () => {
        setIsLoading(true);
        try {
            await auth?.signup(
                {
                    email: form.getFieldValue("email")
                },
                () => {
                    setIsLoading(false)
                    setOpenAgreementModal(false)
                    setOpenSuccessSignupModal(true)
                    // noti?.pushNoti({
                    //     type: 'success',
                    //     message: "Congratulation!",
                    //     description: (
                    //         <div>
                    //             <p>Your account has been successfully registered.</p>
                    //         </div>
                    //     ),
                    //     duration: 0, // will never auto close
                    //     btn: (
                    //         <>
                    //             <Link to="/" replace={true}>Go to login page</Link>
                    //         </>
                    //     ),
                    //     onClose: () => navigate("/", { replace: true })
                    // })
                    //return navigate("/", { replace: false })
                }
            )
        } catch (err: any) {
            setIsLoading(false);
            const {                
                message: { data: { detail }, statusText }
            } = err;
            
            noti?.pushNoti({
                type: 'error',
                message: statusText,
                description: detail,
                duration: 7
            });            
        }
    }

    return (
        <>
            <Particles
                id='tsparticles'
                init={particlesInit}
                options={particleOptions}
            />

            <div style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50 %, -50 %)',
                "WebkitTransform": 'translate(-50%, -50%)',
                "msTransform": 'translate(-50%, -50%)',
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 50,
                    maxWidth: 500,
                }}>
                    <div>
                        {/* LOGO */}
                    </div>
                    <div style={{
                        width: 300,
                        height: '100%',
                    }}>
                        <Form
                            name="signup_form"
                            form={form}
                            className="signup-form"
                            onFinish={onFinish}
                        >
                            <Form.Item
                                name="email"
                                rules={[{ type: 'email', required: true, message: 'Please input your Email' }]}
                            >
                                <Input
                                    prefix={<UserOutlined className="site-form-item-icon" />}
                                    type="email"
                                    placeholder="Email"
                                />
                            </Form.Item>

                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}>
                                <Button loading={isLoading} type="primary" htmlType="submit" className="login-form-button">
                                    Sign up
                                </Button>
                                <span style={{ display: 'block', color: '#FF8300' }}>Or </span>
                                <Button
                                    type="link"
                                    htmlType="button"
                                    // onClick={() => window.open("https://portal.arubainstanton.com/", "_blank")}
                                    onClick={()=>navigate('/', { replace: false })}
                                    style={{ color: '#FF8300' }}
                                >
                                    ← Go back to login page
                                </Button>                                
                                {/* <a href="https://portal.arubainstanton.com/">Go to Aruba portal</a> */}
                            </div>                            
                        </Form>                         
                    
                        <Modal
                            open={openAgreementModal}
                            title="Agreement"
                            footer={[
                                <Button key="back" onClick={() => setOpenAgreementModal(false)}>
                                    Cancel
                                </Button>,
                                <Button key="submit" type="primary" loading={isLoading} onClick={handleAcceptAgreementClick}>
                                Accept and Continue
                                </Button>                                
                            ]}
                            onCancel={() => setOpenAgreementModal(false)}
                        >
                            <ol>
                                <li>
                                    Please make sure that you use the correct email address that is registered at <a href='https://portal.arubainstanton.com'><b>portal.arubainstanton.com</b></a><br />
                                    Is <i><u>{form.getFieldValue("email")}</u></i> correct?
                                </li>
                                <li>Instant Sight does not store any user information, including passwords, emails, or data usage.</li>
                                <li>All user data is stored in Aruba Instant On cloud.</li>
                                <li>Instant Sight will not be responsible for any lost or leaked data under any circumstances.</li>
                                <li>You are responsible for using it at your own risk and needs.</li>
                            </ol>                            
                        </Modal>

                        <Modal
                            open={openSuccessSignupModal}
                            // title="Registered successfully"                            
                            footer={null}
                            onCancel={() => setOpenSuccessSignupModal(false)}
                            centered
                        >
                            <Result 
                                status="success"
                                title="Registered successfully"
                                subTitle={`You have registered to use this tool with email: ${form.getFieldValue("email")}`}
                                extra={[
                                  <Button type="primary" key="lgp" onClick={() => navigate("/")}>
                                    Go To Login Page
                                  </Button>                                  
                                ]}
                            />
                        </Modal>
                                                                     
                    </div>
                </div>
            </div>

            <motion.div
                initial={{ scaleX: 1 }}
                animate={{ scaleX: 0, transition: { duration: 0.5, ease: "circOut" } }}
                exit={{ scaleX: 1, transition: { duration: 0.5, ease: "circIn" } }}
                style={{ originX: isPresent ? 0 : 1 }}
                className="privacy-screen"
            />
        </>
    )
}

export default SignupPage